/**
 * Theme editor.
 *
 * Writes SiteSettings.theme, which the SSR renderer turns into CSS custom
 * properties on the next request. Every Tailwind token on the public site
 * resolves to one of those variables, so a change here restyles the whole site
 * with no rebuild and no redeploy.
 */
import { useEffect, useState } from 'react';
import { DEFAULT_THEME, mergeTheme } from '@agenz/shared/brand';
import { api } from '../lib/api.js';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  PageTitle,
  SaveState,
  Spinner,
} from '../components/ui.jsx';

const COLOR_FIELDS = [
  { key: 'ink', label: 'Ink', hint: 'Backgrounds and display type' },
  { key: 'acid', label: 'Acid', hint: 'The signature accent' },
  { key: 'electric', label: 'Electric', hint: 'Marks, links, emphasis' },
  { key: 'blush', label: 'Blush', hint: 'Secondary accent' },
  { key: 'mint', label: 'Mint', hint: 'Tertiary accent' },
  { key: 'paper', label: 'Paper', hint: 'Page background' },
  { key: 'slate', label: 'Slate', hint: 'Body copy' },
  { key: 'muted', label: 'Muted', hint: 'Secondary text' },
  { key: 'line', label: 'Line', hint: 'Hairlines' },
];

const FONT_FIELDS = [
  { key: 'display', label: 'Display font', hint: 'Headlines. Drop in licensed Brice here.' },
  { key: 'body', label: 'Body font', hint: 'Paragraph text' },
  { key: 'script', label: 'Script font', hint: 'Signature flourishes' },
];

export default function Theme() {
  const [theme, setTheme] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  function load() {
    setError(null);
    api
      .get('/api/admin/settings')
      .then((data) => {
        const merged = mergeTheme(data.settings?.theme ?? {});
        setTheme({ colors: merged.colors, fonts: merged.fonts });
      })
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  function setColor(key, value) {
    setTheme((prev) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  }

  function setFont(key, value) {
    setTheme((prev) => ({ ...prev, fonts: { ...prev.fonts, [key]: value } }));
  }

  async function save() {
    setState('saving');
    setError(null);
    try {
      await api.patch('/api/admin/settings', { theme });
      setState('saved');
      setTimeout(() => setState('idle'), 2500);
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  function reset() {
    setTheme({ colors: { ...DEFAULT_THEME.colors }, fonts: { ...DEFAULT_THEME.fonts } });
  }

  if (error && !theme) return <ErrorNote error={error} onRetry={load} />;
  if (!theme) return <Spinner />;

  return (
    <>
      <PageTitle
        title="Theme"
        description="Changes apply to the live site within a couple of seconds — no rebuild, no deploy. The public site reads these values as CSS variables at render time."
        action={
          <div className="flex items-center gap-4">
            <SaveState state={state} />
            <Button variant="ghost" onClick={reset}>
              Reset to brand
            </Button>
            <Button onClick={save} disabled={state === 'saving'}>
              Save theme
            </Button>
          </div>
        }
      />

      <ErrorNote error={error} />

      <div className="grid gap-8 lg:grid-cols-2">
        <Card className="p-8">
          <h2 className="text-xl">Colours</h2>
          <div className="mt-6 space-y-4">
            {COLOR_FIELDS.map((field) => (
              <div key={field.key} className="flex items-center gap-4">
                <input
                  type="color"
                  aria-label={`${field.label} colour`}
                  value={theme.colors[field.key] ?? '#000000'}
                  onChange={(e) => setColor(field.key, e.target.value.toUpperCase())}
                  className="size-12 shrink-0 cursor-pointer border-3 border-ink bg-paper"
                />
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-widest">{field.label}</p>
                  <p className="text-xs text-muted">{field.hint}</p>
                </div>
                <Input
                  value={theme.colors[field.key] ?? ''}
                  onChange={(e) => setColor(field.key, e.target.value.toUpperCase())}
                  className="w-32 font-mono text-xs"
                />
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-8">
          <Card className="p-8">
            <h2 className="text-xl">Fonts</h2>
            <p className="mt-2 text-sm text-muted">
              CSS font stacks. The deck&apos;s display face, Brice Bold Condensed, is
              commercially licensed — add the files to the site and name it here to switch
              from the Anton stand-in.
            </p>
            <div className="mt-6 space-y-5">
              {FONT_FIELDS.map((field) => (
                <Field key={field.key} label={field.label} hint={field.hint} htmlFor={field.key}>
                  <Input
                    id={field.key}
                    value={theme.fonts[field.key] ?? ''}
                    onChange={(e) => setFont(field.key, e.target.value)}
                    className="font-mono text-xs"
                  />
                </Field>
              ))}
            </div>
          </Card>

          <Card className="overflow-hidden">
            <p className="border-b-3 border-ink p-4 text-xs font-bold uppercase tracking-widest">
              Live preview
            </p>
            <div className="p-8" style={{ backgroundColor: theme.colors.acid }}>
              <p
                className="text-4xl uppercase leading-none"
                style={{ fontFamily: theme.fonts.display, color: theme.colors.ink }}
              >
                Built to Perform.
              </p>
              <p
                className="mt-4 text-sm"
                style={{ fontFamily: theme.fonts.body, color: theme.colors.slate }}
              >
                Every decision starts with strategy. Every idea has a purpose.
              </p>
              <div className="mt-6 flex gap-3">
                {['electric', 'blush', 'mint'].map((key) => (
                  <span
                    key={key}
                    className="size-10 border-3"
                    style={{
                      backgroundColor: theme.colors[key],
                      borderColor: theme.colors.ink,
                    }}
                  />
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
